import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Controller,
  useFormContext,
} from "react-hook-form";
import { useQuery, useApolloClient } from "@apollo/client";
import {
  FormControl,
  TextField,
  CircularProgress,
  Typography,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import debounce from "lodash/debounce";
import { ENTITY_CONFIG } from "./entityQueries";
import {
  CreatedEntity,
  EntityCreateDialog,
  isCreatableResource,
  useCanCreateEntity,
} from "./entityCreateDialog";

/** Sentinel id for the synthetic "Add …" option appended to the search results. */
const CREATE_OPTION_ID = -1;

// Default query used as placeholder when resource is unknown (skipped via skip: true)
const NOOP_QUERY = ENTITY_CONFIG.firm.searchQuery;

interface EntityInputProps {
  resource: string;
  recordKey: string;
  label: string;
  isRequired: boolean;
  allowChange: boolean;
  hidden: boolean;
  multiple?: boolean;
  businessObjectEnumId?: number;
  isLLMPopulated?: boolean;
  autoPopulatedFields?: string[];
  setAutoPopulatedFields?: React.Dispatch<React.SetStateAction<string[]>>;
  /** Document date on this row (YYYY-MM-DD), pre-filled into the create form. */
  documentDate?: string | null;
  /** Form path of this row's firm selection, pre-selected when creating an investment. */
  firmRecordKey?: string;
}

const EMPTY_ARRAY: any[] = [];

const EntitySelector: React.FC<EntityInputProps> = ({
  resource,
  recordKey,
  label,
  isRequired,
  allowChange,
  hidden,
  multiple = false,
  businessObjectEnumId,
  isLLMPopulated = false,
  autoPopulatedFields,
  setAutoPopulatedFields,
  documentDate,
  firmRecordKey,
}) => {
  const formContext = useFormContext();
  const apolloClient = useApolloClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [createName, setCreateName] = useState<string | null>(null);
  const resolvingIdsRef = useRef<Set<number>>(new Set());
  const config = ENTITY_CONFIG[resource];
  const canCreate = useCanCreateEntity(resource) && allowChange && !hidden;

  const handleSearchChange = useMemo(
    () => debounce((term: string) => {
      setSearchTerm(term);
    }, 300),
    []
  );

  const currentValue = formContext?.watch(recordKey);

  // Resolve names for values that only have an id (e.g. from LLM auto-population)
  useEffect(() => {
    if (!config || !formContext) return;

    const values: any[] = multiple
      ? (Array.isArray(currentValue) ? currentValue : [])
      : (currentValue ? [currentValue] : []);

    const needsResolution = values.filter(
      (v: any) => v?.id && !v.name && !resolvingIdsRef.current.has(v.id)
    );
    if (needsResolution.length === 0) return;

    needsResolution.forEach(v => resolvingIdsRef.current.add(v.id));

    Promise.all(
      needsResolution.map(async (v: any) => {
        try {
          const { data } = await apolloClient.query({
            query: config.byIdQuery,
            variables: { id: v.id },
          });
          const entity = data?.[config.byIdPath];
          return {
            id: v.id,
            name: entity?.name || `ID: ${v.id}`,
            ...(entity?.firm ? { firm: entity.firm } : {}),
          };
        } catch {
          return { id: v.id, name: `ID: ${v.id}` };
        }
      })
    ).then(resolved => {
      const resolvedMap = new Map(resolved.map(r => [r.id, r]));
      const fresh = formContext.getValues(recordKey);

      if (multiple && Array.isArray(fresh)) {
        formContext.setValue(
          recordKey,
          fresh.map((v: any) => resolvedMap.get(v.id) ?? v)
        );
      } else if (!multiple && fresh?.id && resolvedMap.has(fresh.id)) {
        formContext.setValue(recordKey, resolvedMap.get(fresh.id));
      }

      needsResolution.forEach(v => resolvingIdsRef.current.delete(v.id));
    });
  }, [currentValue, config, multiple, recordKey, apolloClient, formContext]);

  const shouldSkipSearch = !searchTerm || searchTerm.length < 2 || hidden;

  const { loading, data: searchData } = useQuery(
    config?.searchQuery ?? NOOP_QUERY,
    {
      variables: { searchTerm },
      skip: shouldSkipSearch || !config,
      fetchPolicy: "network-only",
    }
  );

  const searchResults = useMemo(() => {
    if (!searchData || !config) return [];
    return config.getSearchResults(searchData);
  }, [searchData, config]);

  if (!config) {
    return null;
  }

  // Build options: current selections + search results
  const options = useMemo(() => {
    const allOptions: any[] = [];
    const addedIds = new Set<number>();

    // Add current value(s) first
    if (multiple && Array.isArray(currentValue)) {
      for (const item of currentValue) {
        if (item?.id && !addedIds.has(item.id)) {
          allOptions.push(item);
          addedIds.add(item.id);
        }
      }
    } else if (!multiple && currentValue?.id && !addedIds.has(currentValue.id)) {
      allOptions.push(currentValue);
      addedIds.add(currentValue.id);
    }

    // Add search results
    searchResults.forEach((item: any) => {
      if (item?.id && !addedIds.has(item.id)) {
        allOptions.push(item);
        addedIds.add(item.id);
      }
    });

    // Inline create affordance for the searched term — the search-miss escape
    // hatch that keeps the user in the queue (issue #5017). Offered only once
    // the search has actually returned for this term: while results are still
    // in flight "Add …" would be the only row in the list, so a click meant
    // for a real record lands on create instead — and offering create before
    // the matches are known is exactly how duplicates get made.
    if (
      canCreate &&
      !loading &&
      !!searchData &&
      searchTerm.trim().length >= 2
    ) {
      allOptions.push({
        id: CREATE_OPTION_ID,
        name: searchTerm.trim(),
        isCreateOption: true,
      });
    }

    return allOptions;
  }, [
    currentValue,
    searchResults,
    multiple,
    canCreate,
    searchTerm,
    loading,
    searchData,
  ]);

  const isActuallyLLMPopulated =
    isLLMPopulated &&
    ((multiple && Array.isArray(currentValue) && currentValue.length > 0) ||
      (!multiple && currentValue?.id));

  if (hidden || !formContext) return null;

  /** A user-driven value change clears the "AI populated" marker on the field. */
  const clearAutoPopulatedMarker = () => {
    if (setAutoPopulatedFields && autoPopulatedFields?.includes(recordKey)) {
      setAutoPopulatedFields(prev => prev.filter(f => f !== recordKey));
    }
  };

  /**
   * Puts a created entity — or an existing near match chosen instead of
   * creating a duplicate — into this row. Goes through the field's onChange so
   * the row is marked dirty exactly as a normal pick would.
   */
  const handleCreated = (
    field: { value: any; onChange: (value: any) => void },
    entity: CreatedEntity
  ) => {
    const selection = {
      id: entity.id,
      name: entity.name,
      ...(entity.firm
        ? { firm: { id: entity.firm.id, name: entity.firm.name } }
        : {}),
    };
    if (multiple) {
      const existing: any[] = Array.isArray(field.value) ? field.value : [];
      field.onChange(
        existing.some((v: any) => v?.id === selection.id)
          ? existing
          : [...existing, selection]
      );
    } else {
      field.onChange(selection);
    }
    clearAutoPopulatedMarker();
    setCreateName(null);
    setSearchTerm("");
  };

  // Only an unambiguous single firm on the row pre-fills the parent firm.
  const rowFirmValue = firmRecordKey
    ? formContext.getValues(firmRecordKey)
    : undefined;
  const rowFirms: any[] = Array.isArray(rowFirmValue)
    ? rowFirmValue
    : rowFirmValue
      ? [rowFirmValue]
      : [];
  const initialFirm =
    rowFirms.length === 1 && rowFirms[0]?.id && rowFirms[0]?.name
      ? { id: rowFirms[0].id, name: rowFirms[0].name }
      : null;

  return (
    <FormControl fullWidth required={isRequired} size="small">
      <Controller
        name={recordKey}
        rules={{ required: isRequired ? `${label} is required` : false }}
        render={({ field, fieldState }) => {
          return (
            <>
              <Autocomplete
                id={`${recordKey}-select`}
                multiple={multiple}
                size="small"
                options={options}
                getOptionLabel={option => {
                  if (!option) return "";
                  if (option.isCreateOption) return `Add "${option.name}"`;
                  if (!option.name && option.id) return `ID: ${option.id}`;
                  if (!option.name) return "";
                  if (
                    (resource === "investment" ||
                      resource === "firmEngagement") &&
                    option.firm
                  )
                    return `${option.name} - ${option.firm.name}`;
                  return option.name || "";
                }}
                disabled={!allowChange}
                loading={loading}
                filterOptions={x => x}
                onInputChange={(_, newValue, reason) => {
                  if (reason === "input") {
                    handleSearchChange(newValue);
                  }
                }}
                onChange={(_, newValue) => {
                  // The "Add …" row is an action, not a selectable value.
                  const createOption = multiple
                    ? (newValue as any[])?.find((v: any) => v?.isCreateOption)
                    : (newValue as any)?.isCreateOption
                      ? (newValue as any)
                      : null;
                  if (createOption) {
                    setCreateName(createOption.name);
                    return;
                  }
                  field.onChange(newValue);
                  // Remove from auto-populated fields when user changes value
                  clearAutoPopulatedMarker();
                }}
                value={
                  multiple ? field.value || EMPTY_ARRAY : field.value || null
                }
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                freeSolo={false}
                renderInput={params => (
                  <TextField
                    {...(params as any)}
                    label={label}
                    error={!!fieldState.error}
                    variant="outlined"
                    size="small"
                    sx={{
                      ...(isActuallyLLMPopulated && {
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgba(201,186,232,0.5)",
                          "&:hover": {
                            backgroundColor: "rgba(201,186,232,0.5)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(201,186,232,0.5)",
                          },
                        },
                      }),
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) =>
                  option.isCreateOption ? (
                    <li
                      {...props}
                      key={option.id}
                      data-cy={`${recordKey}-create-option`}
                    >
                      <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                      {`Add "${option.name}"`}
                    </li>
                  ) : (
                    <li {...props} key={option.id}>
                      {(resource === "investment" ||
                        resource === "firmEngagement") &&
                      option.firm ? (
                        <>
                          {option.name}
                          <Typography
                            component="span"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.85em",
                              marginLeft: 1,
                              fontStyle: "italic",
                            }}
                          >
                            {` - ${option.firm.name}`}
                          </Typography>
                        </>
                      ) : (
                        option.name
                      )}
                    </li>
                  )
                }
              />
              {fieldState.error && (
                <Typography color="error" variant="caption">
                  {fieldState.error.message}
                </Typography>
              )}
              {createName !== null && isCreatableResource(resource) && (
                <EntityCreateDialog
                  resource={resource}
                  initialName={createName}
                  documentDate={documentDate}
                  initialFirm={resource === "investment" ? initialFirm : null}
                  onClose={() => setCreateName(null)}
                  onCreated={entity => handleCreated(field, entity)}
                />
              )}
            </>
          );
        }}
      />
    </FormControl>
  );
};

export default EntitySelector;
